"use client";
import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { usePathname } from 'next/navigation';
import ReCAPTCHA from "react-google-recaptcha";
import { generateServiceFromPathname, shouldShowServiceDropdown, REQUIRED_SERVICE } from "@/types/constants";

const FREEMAIL_RE = /@(gmail|googlemail|yahoo|hotmail|outlook|aol|icloud|protonmail|gmx|live|mail|yandex|zoho|msn|inbox|fastmail)(\.[a-z]{2,})?$/i;
function isFreemail(email?: string) { return Boolean(email && FREEMAIL_RE.test(email)); }

interface QuoteFormHeaderProps {
  requiredService?: string;
  buttonLabel?: string;
  formTitle?: string;
}

interface QuoteFormHeaderData {
  name: string;
  email_address: string;
  company: string;
  phone_number: string;
  service: string;
  how_did_you_find_us: string;
  enquiry: string;
  marketing: boolean;
  analytics_id: string;
  "WC GCLID": string;
}

interface FormError {
  field: string;
  message: string;
}

interface SubmissionResponse {
  success: boolean;
  message?: string;
  errors?: FormError[];
  recaptcha?: {
    success: boolean;
    challenge_ts?: string;
    hostname?: string;
    "error-codes"?: string[];
  };
  debug?: {
    tokenPreview: string;
    secretKeyPrefix: string;
  };
}

export default function QuoteFormHeader({
  requiredService,
  buttonLabel,
  formTitle
}: QuoteFormHeaderProps) {
  const [formData, setFormData] = useState<QuoteFormHeaderData>({
    name: "",
    email_address: "",
    company: "",
    phone_number: "",
    service: requiredService || "",
    how_did_you_find_us: "",
    enquiry: "",
    marketing: false,
    analytics_id: "GTM-PHZ7KSZ",
    "WC GCLID": "",
  });

  const [errors, setErrors] = useState<FormError[]>([]);
  const [submissionError, setSubmissionError] = useState<string>("");
  const shouldShowSubmissionError = Boolean(submissionError && !errors.some(err => err.message === submissionError));
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const pathname = usePathname();
  const currentService = generateServiceFromPathname(pathname);
  const showServiceDropdown = shouldShowServiceDropdown(pathname);
  const [csrfToken, setCsrfToken] = useState<string>('');

  useEffect(() => {
    const analyticsId = localStorage.getItem("_ga");
    if (analyticsId) {
      setFormData(prev => ({
        ...prev,
        analytics_id: analyticsId,
        service: currentService === null ? "" : currentService
      }));
    }
  }, [currentService]);

  useEffect(() => {
    fetch('/api/csrf')
      .then(res => {
        const token = res.headers.get('x-csrf-token');
        if (token) {
          setCsrfToken(token);
        }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const getGclidParam = () => {
      if (typeof window === 'undefined') return null;
      const match = RegExp('[?&]gclid=([^&]*)').exec(window.location.search);
      return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : null;
    };

    const shouldStoreGclid = () => {
      const gclsrc = new URLSearchParams(window.location.search).get('gclsrc');
      return !gclsrc || gclsrc.indexOf('aw') !== -1;
    };

    const gclidParam = getGclidParam();

    if (gclidParam && shouldStoreGclid()) {
      const expiryDate = new Date().getTime() + (90 * 24 * 60 * 60 * 1000);
      const gclidData = {
        value: gclidParam,
        expiryDate: expiryDate
      };
      localStorage.setItem('gclid', JSON.stringify(gclidData));

      setFormData(prev => ({
        ...prev,
        "WC GCLID": gclidParam
      }));
    } else {
      const storedGclid = localStorage.getItem('gclid');
      if (storedGclid) {
        const gclidData = JSON.parse(storedGclid);
        if (new Date().getTime() < gclidData.expiryDate) {
          setFormData(prev => ({
            ...prev,
            "WC GCLID": gclidData.value
          }));
        } else {
          localStorage.removeItem('gclid');
        }
      }
    }
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const inputValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: inputValue,
    }));
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[QuoteHeader] handleSubmit called');
    setErrors([]);
    setSubmissionError("");
    try {
      if (isFreemail(formData.email_address)) {
        const msg = 'Business emails only. Please use a company email address.';
        setErrors([{ field: 'email_address', message: msg }]);
        setSubmissionError(msg);
        return;
      }
    } catch { /* ignore */ }

    // Read token directly from the DOM — same source Zapier reads
    const currentToken = document.querySelector<HTMLTextAreaElement>('textarea[name="g-recaptcha-response"]')?.value
      || recaptchaRef.current?.getValue()
      || recaptchaToken;
    if (!currentToken) {
      setSubmissionError("Please complete the reCAPTCHA verification.");
      return;
    }
    setIsSubmitting(true);

    // Get browser info
    const userAgent = navigator.userAgent;

    // Prepare form data
    const submissionData = {
      ...formData,
      user_agent: userAgent,
      service: formData.service || "Penetration Testing",
    };

    console.log('[QuoteHeader] Submitting form data:', submissionData);
    console.log('[QuoteHeader] recaptchaToken length:', currentToken.length);

    try {
      const response = await fetch("/api/quote", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          ...formData,
          form_name: "quote",
          form_url: window.location.href,
          recaptchaToken: currentToken,
        }),
      });
      // const data = await response.json();

      console.log('[QuoteHeader] Response status:', response.status);

      // Check if response is JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Response was not JSON");
      }

      const data: SubmissionResponse = await response.json();
      console.log('[QuoteHeader] Response data:', data);
      console.log('[reCAPTCHA] siteverify response:', data.recaptcha ?? 'not included in response');
      console.log('[reCAPTCHA] server debug:', data.debug ?? 'not included in response');

      if (!response.ok) {
        console.error("[QuoteHeader] Submission failed:", {
          status: response.status,
          message: data.message,
          errors: data.errors,
          recaptcha: data.recaptcha,
        });
        if (data.errors) {
          setErrors(data.errors);
        } else {
          setSubmissionError(data.message || "An error occurred while submitting the form.");
        }
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
        return;
      }

      setIsSubmitted(true);
      setFormData({
        name: "",
        email_address: "",
        company: "",
        phone_number: "",
        service: requiredService || "",
        how_did_you_find_us: "",
        enquiry: "",
        marketing: false,
        analytics_id: "GTM-PHZ7KSZ",
        "WC GCLID": "",
      });
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);

    } catch (error) {
      console.error('[QuoteHeader] Submit error:', error);
      setSubmissionError("An error occurred while submitting the form.");
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const preSubmitCheck = (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      if (isFreemail(formData.email_address)) {
        e.preventDefault();
        e.stopPropagation();
        const msg = 'Business emails only. Please use a company email address.';
        setErrors([{ field: 'email_address', message: msg }]);
        setSubmissionError(msg);
        return false;
      }
    } catch { /* ignore */ }
    return true;
  };

  const hasRequiredFields = () => {
    if (!formData.name?.trim()) return false;
    if (!formData.email_address?.trim()) return false;
    if (!formData.company?.trim()) return false;
    if (!formData.enquiry?.trim()) return false;
    if (showServiceDropdown && (!formData.service || formData.service === "")) return false;
    return true;
  };
  const canSubmit = () => !isSubmitting && Boolean(recaptchaToken) && hasRequiredFields();

  if (isSubmitted) {
    return (
      <div className="col-lg-10 offset-lg-1">
        <div className="card card-lighter p-3 p-md-4">
          <div className="alert alert-success w-100 text-center">
            <h4>Thank you for your enquiry</h4>
            <p>We&apos;ll be in touch shortly.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-lg-10 offset-lg-1">
      <div className="card card-lighter p-3 p-md-4">
        <h2 className="h3 mb-3 text-center">
          {formTitle}
        </h2>

        {/* Conditional Error Display */}
        {errors.length > 0 && (
          <div className="d-flex justify-content-center">
            <div className="alert alert-error w-100">
              {errors.map((err, i) => (
                <p className="mb-0" key={i}>
                  {err.message}
                </p>
              ))}
            </div>
          </div>
        )}

        {shouldShowSubmissionError && (
          <div className="d-flex justify-content-center">
            <div className="alert alert-shifted alert-error">
              <p>{submissionError}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="fast-quote">
          {/* Hidden Input Fields */}
          <input
            type="hidden"
            name="how_did_you_find_us"
            value={formData.how_did_you_find_us}
            aria-label="how_did_you_find_us"
          />
          <input type="hidden" name="anchor" value="#quickQuote" aria-label="anchor" />

          <div className="row gy-2 mb-2 gx-2">
            {/* Each input field structured similarly to Rails */}
            <div className="col-24 col-md-12">
              <input
                type="text"
                name="name"
                className="form-input"
                id="quote1_name"
                placeholder="Your name *"
                aria-label="Your name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="col-24 col-md-12">
              <input
                type="email"
                name="email_address"
                className="form-input"
                id="quote1_email_address"
                placeholder="Email address *"
                value={formData.email_address}
                onChange={handleInputChange}
                required
                aria-label="Email address"
              />
            </div>
            <div className="col-24 col-md-12">
              <input
                type="text"
                name="company"
                className="form-input"
                id="quote1_company"
                placeholder="Company name *"
                value={formData.company}
                onChange={handleInputChange}
                required
                aria-label="Company name"

              />
            </div>
            <div className="col-24 col-md-12">
              <input
                type="tel"
                name="phone_number"
                className="form-input"
                id="quote1_phone_number"
                placeholder="Telephone"
                value={formData.phone_number}
                onChange={handleInputChange}
                aria-label="Telephone"
              />
            </div>
            {showServiceDropdown ? (
              <div className="col-24">
                <label htmlFor="quote1_service" className="styled-select">
                  <select
                    name="service"
                    id="quote1_service"
                    className="form-input"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    aria-label="Required service"
                    aria-required="true"
                    aria-invalid={formData.service === ''}
                  >
                    <option value="">Please select required service *</option>
                    {REQUIRED_SERVICE.map((service, i) => (
                      <option key={i} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : (
              <div className="d-none">
                <input
                  type="hidden"
                  name="service"
                  value={formData.service}
                  aria-label="service"
                />
              </div>
            )}
            <div className="col-24">
              <textarea
                rows={3}
                maxLength={1500}
                name="enquiry"
                id="quote1_enquiry"
                className="form-input"
                placeholder="Comments *"
                value={formData.enquiry}
                onChange={handleInputChange}
                required
              ></textarea>
            </div>

            <div className="col-24">
              <label className="small text-dark mt-2">
                <input
                  type="checkbox"
                  name="marketing"
                  id="quote1_marketing"
                  className="me-2"
                  checked={formData.marketing}
                  onChange={handleInputChange}
                  aria-label="marketing"
                />
                I agree to receive sales and marketing communication. For more
                information about how we collect, process, and retain your
                personal data, please see our{" "}
                <Link href="/privacy-notice" target="_blank">
                  privacy policy
                </Link>
                .
              </label>
            </div>
          </div>

          {/* Extra Hidden Input */}
          <input
            type="hidden"
            name="analytics_id"
            value={formData.analytics_id}
            className="analytics_id"
            aria-label="analytics_id"
          />
          <input
            type="hidden"
            id="gclid_field"
            name="WC GCLID"
            value={formData["WC GCLID"]}
          />

          {/* reCAPTCHA */}
          <div className="d-flex justify-content-center mb-3">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
              onChange={handleRecaptchaChange}
            />
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              className="button button-large button-secondary w-100"
              disabled={!canSubmit()}
              onClick={preSubmitCheck}
              style={!canSubmit() ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
            >
              {isSubmitting ? "Submitting..." : buttonLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
